/**
 * @file: chatCardDescriptor.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Derives the DISPLAY model for a task card (order / service-record /
 * report) from its structured payload data. The payloads carry data
 * only (vehicle snapshot, ids, report type) — never `title`/`subtitle`
 * — so all "how to render it" decisions live here, in one place that can
 * evolve without a payload schema change. Mirrors how
 * `feedMessageDescriptor` turns typed feed params into a renderable
 * descriptor.
 */

import { reportTypeLabel } from '@euler/app/components/entity-card-picker/ReportTypeChooser';
import { StatusPaletteKey } from '@euler/app/components/task-card/types';
import { deriveStatusBadge } from '@euler/app/components/task-card/utils';
import { ReportContentType } from '@euler/model/ReportContentType';
import { TaskListItem } from '@euler/model/TaskListItem';
import {
  ChatCardVehicleInfo,
  ChatMessagePayloadOrderCard,
  ChatMessagePayloadReportCard,
  ChatMessagePayloadServiceRecordCard,
  ChatTaskCardBase,
} from '@euler/model/chat';
import { TaskDetail } from '@euler/model/task-detail';
import { dateFromValue } from '@euler/utils/datetime';

/** Normalize a date-ish snapshot value to an ISO string (or undefined). */
function toIso(v: string | Date | null | undefined): string | undefined {
  if (v == null) return undefined;
  const d = dateFromValue(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export type ChatCardPayload =
  | ChatMessagePayloadOrderCard
  | ChatMessagePayloadServiceRecordCard
  | ChatMessagePayloadReportCard;

export interface ChatCardMetaRow {
  label: string;
  value: string;
}

export interface ChatCardView {
  kind: 'order' | 'service_record' | 'report';
  /** Card category label shown in the footer ("工单" / "服务记录" / "检测报告"). */
  footerLabel: string;
  /** Primary line — canonical vehicle name, else order no. */
  title: string;
  /** License plate, rendered as a badge under the title. */
  plate?: string;
  /** Highlight badge (the specific report type, for report cards). */
  badge?: string;
  /** Secondary label/value rows (only those with a value). */
  metaRows: ChatCardMetaRow[];
  /** Brand logo URL — the card's leading visual. */
  brandLogo?: string;
  /** Status palette key for the left accent bar + status badge. */
  statusTone?: StatusPaletteKey;
  /** Localized status label (e.g. "施工中"). */
  statusLabel?: string;
}

/** Build the embedded vehicle snapshot for a card from a task list item. */
export function vehicleInfoFromTaskItem(t: TaskListItem): ChatCardVehicleInfo {
  return {
    name: t.vehicleName || undefined,
    licensePlateNo: t.licensePlateNo || undefined,
    vin: t.vin || undefined,
    brandName: t.vehicleBrandName || undefined,
    brandLogo: t.vehicleBrandLogo,
    manufacturer: t.vehicleManufacturer,
    model: t.vehicleModel,
    modelYear: t.vehicleModelYear,
    fuelType: t.vehicleFuelType,
    displacement: t.vehicleDisplacement,
    trimLevel: t.vehicleTrimLevel,
    mileage: t.vehicleMileage,
    imageUrl: t.vehicleImageUrl,
  };
}

/**
 * Build the shared task-card base (ids + agent + vehicle snapshot) from a
 * task list item. The concrete card send-sites spread this and add their
 * own deep-link key (serviceRecordId / reportNo+reportType).
 */
export function taskCardBaseFromItem(t: TaskListItem): ChatTaskCardBase {
  // Snapshot the same derived status the task card shows, at share time.
  const badge = deriveStatusBadge(t);
  return {
    orgId: t.orgId,
    storeId: t.storeId,
    orderNo: t.orderNo,
    // taskNo only when the item is still an open task; finished tasks
    // surface as a 'service-record' item and deep-link via serviceRecordId
    // instead, so an order card built from one has no task to open.
    taskNo: t.type === 'task' ? t.taskNo : undefined,
    reportNo: t.reportNo || undefined,
    customerName: t.customerName || undefined,
    serviceAgentId: t.serviceAgentId,
    serviceAgentName: t.serviceAgentName || undefined,
    orderedAt: toIso(t.orderedAt),
    finishedAt: toIso(t.finishedAt),
    status: badge ? { label: badge.label, tone: badge.palette } : undefined,
    vehicle: vehicleInfoFromTaskItem(t),
  };
}

/**
 * Build a fresh task-card base from live `TaskDetail` (the revalidation
 * source). Mirrors {@link taskCardBaseFromItem} but sourced from
 * `basicInfo` + the stage objects, so the status badge stays exact.
 */
export function taskCardBaseFromTaskDetail(
  detail: TaskDetail,
): ChatTaskCardBase {
  const b = detail.basicInfo;
  const badge = deriveStatusBadge({
    isRollback: b.isRollback,
    status: b.status,
    isMainTask: b.isMainTask,
    hasChildTasks: b.hasChildOrders,
    preInspectionStatus: detail.preInspection?.status,
    inspectionStatus: detail.inspection?.status,
    constructionStatus: detail.construction?.status,
    isConstructionJobScheduled: detail.construction?.isScheduled,
    deliveryCheckStatus: detail.deliveryCheck?.status,
  });
  return {
    orgId: b.orgId,
    storeId: b.storeId,
    orderNo: b.orderNo,
    taskNo: b.taskNo,
    reportNo: b.reportNo || undefined,
    customerName: b.customerName || undefined,
    serviceAgentId: b.serviceAgentId,
    serviceAgentName: b.serviceAgentName || undefined,
    orderedAt: toIso(b.createdAt),
    finishedAt: toIso(b.finishedAt),
    status: badge ? { label: badge.label, tone: badge.palette } : undefined,
    vehicle: {
      name: b.vehicleName || undefined,
      licensePlateNo: b.licensePlateNo || undefined,
      vin: b.vin || undefined,
      brandName: b.vehicleBrandName || undefined,
      brandLogo: b.vehicleBrandLogo,
      manufacturer: b.vehicleManufacturer,
      model: b.vehicleModel,
      modelYear: b.vehicleModelYear,
      fuelType: b.vehicleFuelType,
      displacement: b.vehicleDisplacement,
      trimLevel: b.vehicleTrimLevel,
      mileage: b.vehicleMileage,
      imageUrl: b.vehicleImageUrl,
    },
  };
}

/** Compose a vehicle's canonical display name from its snapshot fields. */
export function vehicleDisplayName(v: ChatCardVehicleInfo | undefined): string {
  if (!v) return '';
  if (v.name?.trim()) return v.name.trim();
  const composed = [v.brandName, v.model, v.modelYear ? `${v.modelYear}款` : '']
    .map(s => (s ?? '').toString().trim())
    .filter(Boolean)
    .join(' ');
  return composed || v.licensePlateNo?.trim() || '';
}

function row(
  label: string,
  value: string | undefined | null,
): ChatCardMetaRow[] {
  const v = value?.toString().trim();
  return v ? [{ label, value: v }] : [];
}

/**
 * Build the render model for a task card. Pure data → display; safe to
 * call on any of the three card payload variants.
 */
export function describeChatCard(payload: ChatCardPayload): ChatCardView {
  const v = payload.vehicle;
  // vehicleDisplayName already falls back to the plate internally.
  const title = vehicleDisplayName(v) || payload.orderNo || '';
  const plate = v?.licensePlateNo?.trim() || undefined;

  // Plate is promoted to a badge; customer/agent stay as meta rows.
  const meta: ChatCardMetaRow[] = [
    ...row('客户', payload.customerName),
    ...row('服务顾问', payload.serviceAgentName),
  ];

  const common = {
    plate,
    brandLogo: v?.brandLogo,
    statusTone: payload.status?.tone,
    statusLabel: payload.status?.label,
  };

  switch (payload.type) {
    case 'order_card':
      return {
        ...common,
        kind: 'order',
        footerLabel: '工单',
        title: title || '工单',
        metaRows: [...meta, ...row('工单号', payload.orderNo)],
      };
    case 'service_record_card':
      return {
        ...common,
        kind: 'service_record',
        footerLabel: '服务记录',
        title: title || '服务记录',
        metaRows: [...meta, ...row('工单号', payload.orderNo)],
      };
    case 'report_card': {
      const typeLabel = reportTypeLabel(
        payload.reportType as ReportContentType,
      );
      return {
        ...common,
        kind: 'report',
        footerLabel: '检测报告',
        title: title || '检测报告',
        badge: typeLabel,
        metaRows: meta,
      };
    }
  }
}
